import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import CatalogImage from "../common/CatalogImage";
import {
  bootstrapPublicDeliveryWeCustomer,
  createPublicDeliveryOrder,
  identifyPublicDeliveryCustomer,
  locatePublicDeliveryAddress,
  loadPublicDeliveryMenu,
  loadPublicDeliverySession,
  registerPublicDeliveryCustomer,
  validatePublicDeliveryAddress,
} from "../../services/supabase/publicDeliveryService";
import { getWeCustomerAuthState } from "../../services/supabase/weCustomerAuthService";
import {
  rememberWeCustomerOrder,
  resolveWePedidosNavigationUrl,
} from "../../services/supabase/wePedidosService";
import {
  buildPublicMenuAssistantOrderDraft,
  createPublicMenuAssistantReply,
} from "../../services/supabase/publicMenuAssistantService";
import { WE_PEDIDOS_LOGO_URL as wePedidosLogo } from "../../services/local/institutionalBranding";
import {
  buildPromotionPricing,
  isDailyMenuPromotion,
} from "../../services/promotions/promotionEngine";
import DeliveryLocationPicker from "../we-pedidos/DeliveryLocationPicker";
import useWePedidosBranding from "../we-pedidos/useWePedidosBranding";
import useModalBackNavigation from "../common/useModalBackNavigation";
import {
  formatDeliveryCheckoutAddress,
  resolveAccessibleDeliveryBrandColor,
} from "../../services/local/deliveryCheckout";
import "../../styles/DeliveryPublic.css";

const moneyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 2,
});

const PRODUCT_CUSTOMIZATION_HISTORY_STATE_KEY =
  "mozzoappDeliveryProductCustomization";
const ORDER_REVIEW_HISTORY_STATE_KEY = "mozzoappDeliveryOrderReview";

const emptyProfile = {
  firstName: "",
  lastName: "",
  street: "",
  streetNumber: "",
  floor: "",
  unit: "",
  neighborhood: "",
  city: "",
  province: "",
  postalCode: "",
  reference: "",
  latitude: null,
  longitude: null,
};

const deliveryAddressFields = [
  "street",
  "streetNumber",
  "floor",
  "unit",
  "neighborhood",
  "city",
  "province",
  "postalCode",
  "reference",
  "latitude",
  "longitude",
];

const addressPositionFields = new Set([
  "street",
  "streetNumber",
  "neighborhood",
  "city",
  "province",
  "postalCode",
]);

const splitSavedAddressLine = (address = {}) => {
  const explicitStreet = String(address.street || "").trim();
  const explicitNumber = String(address.streetNumber || "").trim();
  if (explicitStreet && explicitNumber) {
    return { street: explicitStreet, streetNumber: explicitNumber };
  }

  const firstLine = String(address.addressLine || "").split(",")[0].trim();
  const match = /^(.*?\D)\s+(\d+[a-zA-Z]?)$/.exec(firstLine);
  return {
    street: explicitStreet || match?.[1]?.trim() || firstLine,
    streetNumber: explicitNumber || match?.[2] || "",
  };
};

const requestCurrentBrowserLocation = () =>
  new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Este dispositivo no permite obtener la ubicacion actual."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      (locationError) => {
        const message =
          locationError?.code === 1
            ? "Habilita el permiso de ubicacion del navegador para continuar."
            : locationError?.code === 3
              ? "La ubicacion tardo demasiado. Intenta otra vez o elegila en el mapa."
              : "No pudimos obtener tu ubicacion. Elegila manualmente en el mapa.";
        reject(new Error(message));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  });

const hasDeliveryAddressChanged = (currentProfile, savedAddress) =>
  deliveryAddressFields.some(
    (field) =>
      String(currentProfile?.[field] || "").trim() !==
      String(savedAddress?.[field] || "").trim()
  );

const getSessionStorageKey = (deliveryToken) =>
  `mozzo:public-delivery-session:${String(deliveryToken || "").trim()}`;

const createRequestId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const toNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toMoney = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

const normalizeMenuSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const singularizeMenuToken = (token) => {
  if (token.length > 5 && token.endsWith("es")) return token.slice(0, -2);
  if (token.length > 4 && token.endsWith("s")) return token.slice(0, -1);
  return token;
};

const matchesMenuSearch = (value, search) => {
  const normalizedValue = normalizeMenuSearch(value);
  const queryTokens = normalizeMenuSearch(search).split(/\s+/).filter(Boolean);
  const valueTokens = normalizedValue.split(/\s+/).filter(Boolean);
  return queryTokens.every((queryToken) => {
    if (normalizedValue.includes(queryToken)) return true;
    const singularQuery = singularizeMenuToken(queryToken);
    return valueTokens.some((valueToken) => {
      const singularValue = singularizeMenuToken(valueToken);
      return singularValue.includes(singularQuery);
    });
  });
};

const getProductIngredients = (product) =>
  Array.isArray(product?.ingredients) ? product.ingredients.filter((ingredient) => ingredient?.id) : [];

const getIngredientBaseQuantity = (ingredient) =>
  Math.max(0, toNumber(ingredient?.baseQuantity));

const getIngredientMaximumQuantity = (ingredient) => {
  const baseQuantity = getIngredientBaseQuantity(ingredient);
  return ingredient?.canAddMore
    ? baseQuantity + Math.max(0, toNumber(ingredient?.maxExtraQuantity))
    : baseQuantity;
};

const isProductCustomizable = (product) =>
  getProductIngredients(product).some(
    (ingredient) => ingredient?.canRemove === true || ingredient?.canAddMore === true
  );

const createIngredientSelection = (product, currentSelection = null) =>
  getProductIngredients(product).reduce((selection, ingredient) => {
    const ingredientId = String(ingredient.id);
    const savedQuantity = currentSelection?.[ingredientId];
    selection[ingredientId] = Number.isFinite(Number(savedQuantity))
      ? Math.max(0, Number(savedQuantity))
      : getIngredientBaseQuantity(ingredient);
    return selection;
  }, {});

const buildCustomizationSummary = (product, selection = {}) => {
  const details = [];
  let extraPrice = 0;

  getProductIngredients(product).forEach((ingredient) => {
    const ingredientId = String(ingredient.id);
    const baseQuantity = getIngredientBaseQuantity(ingredient);
    const selectedQuantity = Number.isFinite(Number(selection[ingredientId]))
      ? Math.max(0, Number(selection[ingredientId]))
      : baseQuantity;
    const name = String(ingredient.name || "Ingrediente");

    if (selectedQuantity < baseQuantity) {
      details.push(
        selectedQuantity === 0
          ? `Sin ${name}`
          : `-${baseQuantity - selectedQuantity} ${name}`
      );
    } else if (selectedQuantity > baseQuantity) {
      const extraQuantity = selectedQuantity - baseQuantity;
      details.push(`+${extraQuantity} ${name}`);
      extraPrice += extraQuantity * toNumber(ingredient.price);
    }
  });

  return { details, extraPrice: toMoney(extraPrice) };
};

function DeliveryPublic() {
  const { deliveryToken = "" } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [menuData, setMenuData] = useState(null);
  const [paymentAvailability, setPaymentAvailability] = useState({
    mercadoPago: false,
    mercadoPagoMessage: "",
  });
  const [sessionToken, setSessionToken] = useState("");
  const [phone, setPhone] = useState("");
  const [profile, setProfile] = useState(emptyProfile);
  const [profileInfo, setProfileInfo] = useState(null);
  const [deliveryZoneId, setDeliveryZoneId] = useState("");
  const [coverageCheck, setCoverageCheck] = useState(null);
  const [validatingAddress, setValidatingAddress] = useState(false);
  const [view, setView] = useState("identify");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [menuSearch, setMenuSearch] = useState(
    () => searchParams.get("dish") || ""
  );
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutReviewPaymentMethod, setCheckoutReviewPaymentMethod] =
    useState("");
  const [chatQuestion, setChatQuestion] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatProductIds, setChatProductIds] = useState([]);
  const [customerNotes, setCustomerNotes] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [customerMatch, setCustomerMatch] = useState(null);
  const [addressEditOrigin, setAddressEditOrigin] = useState(null);
  const [addressSavePrompt, setAddressSavePrompt] = useState(false);
  const [customization, setCustomization] = useState(null);
  const [ingredientSelection, setIngredientSelection] = useState({});
  const [customizationNotes, setCustomizationNotes] = useState("");
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [resolvingLocation, setResolvingLocation] = useState(false);
  const resetCustomization = useCallback(() => {
    setCustomization(null);
    setIngredientSelection({});
    setCustomizationNotes("");
  }, []);
  const closeCustomization = useModalBackNavigation({
    isOpen: Boolean(customization?.product),
    onClose: resetCustomization,
    stateKey: PRODUCT_CUSTOMIZATION_HISTORY_STATE_KEY,
  });
  const resetCheckoutReview = useCallback(() => {
    setCheckoutReviewPaymentMethod("");
  }, []);
  const closeCheckoutReview = useModalBackNavigation({
    isOpen: Boolean(checkoutReviewPaymentMethod),
    onClose: resetCheckoutReview,
    stateKey: ORDER_REVIEW_HISTORY_STATE_KEY,
  });

  const branch = menuData?.branch || {};
  const delivery = menuData?.delivery || {};
  const deliveryZones = Array.isArray(delivery.zones) ? delivery.zones : [];
  const coverageConfig = delivery.coverage || {};
  const profileLocation =
    profile.latitude !== null &&
    profile.longitude !== null &&
    Number.isFinite(Number(profile.latitude)) &&
    Number.isFinite(Number(profile.longitude))
      ? {
          latitude: Number(profile.latitude),
          longitude: Number(profile.longitude),
        }
      : null;
  const restaurantLocation =
    coverageConfig.originLatitude !== null &&
    coverageConfig.originLongitude !== null &&
    Number.isFinite(Number(coverageConfig.originLatitude)) &&
    Number.isFinite(Number(coverageConfig.originLongitude))
      ? {
          latitude: Number(coverageConfig.originLatitude),
          longitude: Number(coverageConfig.originLongitude),
        }
      : null;
  const usesDistanceRates = coverageConfig.radiusEnabled === true;
  const deliveryDistanceRates = Array.isArray(delivery.distanceRates)
    ? delivery.distanceRates
    : [];
  const selectedDeliveryZone = deliveryZones.find(
    (zone) => String(zone?.id || "") === deliveryZoneId
  );
  const hasDeliveryZones = deliveryZones.length > 0;
  const hasDeliveryDistanceRates = deliveryDistanceRates.length > 0;
  const hasDeliveryPricing = usesDistanceRates
    ? hasDeliveryDistanceRates
    : hasDeliveryZones;
  const requiresDeliveryZone = !usesDistanceRates;
  const products = Array.isArray(menuData?.products) ? menuData.products : [];
  const promotions = Array.isArray(menuData?.promotions) ? menuData.promotions : [];
  const restaurantOpen = menuData?.availability?.isOpen !== false;
  const mercadoPagoAvailable = paymentAvailability.mercadoPago === true;
  const deliveryCategoryIds = new Set(
    products.map((product) => product.categoryId).filter(Boolean)
  );
  const categories = (Array.isArray(menuData?.categories)
    ? menuData.categories
    : []
  ).filter((category) => deliveryCategoryIds.has(category.id));
  const normalizedMenuSearch = normalizeMenuSearch(menuSearch);
  const visibleProducts = products.filter((product) => {
    if (selectedCategoryId && product.categoryId !== selectedCategoryId) return false;
    if (!normalizedMenuSearch) return true;
    return matchesMenuSearch(
      [
        product.name,
        product.description,
        product.categoryName,
        product.subcategoryName,
      ].join(" "),
      normalizedMenuSearch
    );
  });
  const visiblePromotions = promotions.filter((promotion) => {
    const promotionCategoryIds = new Set(
      Array.isArray(promotion?.items)
        ? promotion.items
            .map((item) => products.find((product) => product.id === item.productId)?.categoryId)
            .filter(Boolean)
        : []
    );
    if (
      selectedCategoryId &&
      promotion.categoryId !== selectedCategoryId &&
      !promotionCategoryIds.has(selectedCategoryId)
    ) {
      return false;
    }
    if (!normalizedMenuSearch) return true;
    return matchesMenuSearch(
      [
        promotion.title,
        promotion.hook,
        promotion.detail,
        promotion.pricingSummary,
        promotion.categoryName,
        promotion.subcategoryName,
        ...(Array.isArray(promotion.items)
          ? promotion.items.map((item) => item.name)
          : []),
      ].join(" "),
      normalizedMenuSearch
    );
  });
  const promotionPricing = buildPromotionPricing(
    cart.map((item) => ({
      ...item,
      unitPrice: toMoney(item.price),
      baseUnitPrice: toMoney(item.basePrice),
    })),
    promotions,
    products,
    checkoutReviewPaymentMethod || "cash"
  );
  const listSubtotal = toMoney(promotionPricing.listTotal);
  const promotionDiscountTotal = toMoney(promotionPricing.totalDiscount);
  const subtotal = toMoney(promotionPricing.finalTotal);
  const deliveryFee =
    profileInfo?.delivery?.status === "ready"
      ? toNumber(profileInfo.delivery.flatFee)
      : 0;
  const freeFrom = toNumber(profileInfo?.delivery?.freeFrom);
  const effectiveDeliveryFee = freeFrom > 0 && subtotal >= freeFrom ? 0 : deliveryFee;
  const total = subtotal + effectiveDeliveryFee;
  const profileReady = profileInfo?.delivery?.status === "ready";
  const fromWePedidos = searchParams.get("entry") === "we_pedidos";
  useWePedidosBranding("Carta", fromWePedidos);
  const requestedEntry =
    searchParams.get("entry") === "chatbot" && delivery.chatbotEnabled !== false
      ? "chat"
      : "menu";

  useEffect(() => {
    if (!cart.length || !["menu", "chat"].includes(view)) {
      setCartOpen(false);
    }
  }, [cart.length, view]);

  useEffect(() => {
    if (!cartOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setCartOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [cartOpen]);

  const applyProfileResponse = (response) => {
    const nextProfile = {
      ...emptyProfile,
      ...(response?.profile || {}),
      ...(response?.address || {}),
    };
    setProfile(nextProfile);
    setProfileInfo({
      profile: response?.profile || null,
      address: response?.address || null,
      delivery: response?.delivery || null,
    });
    setDeliveryZoneId(String(response?.delivery?.zoneId || ""));
  };

  useEffect(() => {
    let mounted = true;

    const restoreSavedSession = async (token) => {
      const response = await loadPublicDeliverySession({
        deliveryToken,
        sessionToken: token,
      });
      if (!mounted) return;
      applyProfileResponse(response);
      setView(
        response?.needsRegistration ||
        response?.needsDeliveryZone ||
        response?.needsDeliveryPricing ||
        response?.delivery?.status !== "ready"
          ? "profile"
          : requestedEntry
      );
    };

    const restoreWeCustomer = async () => {
      if (!fromWePedidos) return false;
      const weState = await getWeCustomerAuthState();
      if (!weState?.session || !weState?.profile) return false;

      const accountAddress =
        weState.profile.addresses?.find((address) => address.isDefault) ||
        weState.profile.addresses?.[0] ||
        null;
      const accountAddressParts = splitSavedAddressLine(accountAddress || {});
      let response;

      if (weState.session.access_token) {
        response = await bootstrapPublicDeliveryWeCustomer({
          deliveryToken,
          accessToken: weState.session.access_token,
        });
      } else {
        response = await identifyPublicDeliveryCustomer({
          deliveryToken,
          phone: weState.profile.phone,
        });
        response = {
          ...response,
          profile: response?.profile || {
            firstName: weState.profile.firstName,
            lastName: weState.profile.lastName,
          },
          address: response?.address || (accountAddress
            ? {
                id: accountAddress.id,
                street: accountAddressParts.street,
                streetNumber: accountAddressParts.streetNumber,
                floor: accountAddress.floor,
                unit: accountAddress.unit,
                neighborhood: accountAddress.neighborhood,
                city: accountAddress.city,
                province: accountAddress.province,
                postalCode: accountAddress.postalCode,
                reference: accountAddress.reference,
                latitude: accountAddress.latitude,
                longitude: accountAddress.longitude,
              }
            : null),
        };
      }

      const token = response?.session?.sessionToken || "";
      if (!token) return false;
      if (!mounted) return true;
      window.sessionStorage.setItem(getSessionStorageKey(deliveryToken), token);
      setSessionToken(token);
      setPhone(weState.profile.phone || "");
      applyProfileResponse(response);
      setView(
        response?.needsRegistration ||
        response?.needsDeliveryZone ||
        response?.needsDeliveryPricing ||
        response?.delivery?.status !== "ready"
          ? "profile"
          : requestedEntry
      );
      return true;
    };

    const boot = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await loadPublicDeliveryMenu(deliveryToken);
        if (!mounted) return;
        setMenuData(response.menu || null);
        setPaymentAvailability({
          mercadoPago: response?.payment?.mercadoPago === true,
          mercadoPagoMessage: String(response?.payment?.mercadoPagoMessage || ""),
        });

        try {
          if (await restoreWeCustomer()) return;
        } catch (weError) {
          const weState = await getWeCustomerAuthState().catch(() => null);
          if (mounted && weState?.profile?.phone) {
            setPhone(weState.profile.phone);
          }
        }

        const savedSession = window.sessionStorage.getItem(
          getSessionStorageKey(deliveryToken)
        );
        if (savedSession) {
          setSessionToken(savedSession);
          try {
            await restoreSavedSession(savedSession);
          } catch {
            window.sessionStorage.removeItem(getSessionStorageKey(deliveryToken));
            setSessionToken("");
            setView("identify");
          }
        }
      } catch (requestError) {
        if (mounted) {
          setError(requestError?.message || "No pudimos abrir Delivery.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    boot();
    return () => {
      mounted = false;
    };
  }, [deliveryToken, fromWePedidos, requestedEntry]);

  const handleIdentify = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await identifyPublicDeliveryCustomer({ deliveryToken, phone });
      const token = response?.session?.sessionToken || "";
      if (!token) throw new Error("No pudimos iniciar tu sesion de Delivery.");
      window.sessionStorage.setItem(getSessionStorageKey(deliveryToken), token);
      setSessionToken(token);
      applyProfileResponse(response);
      const nextView =
        response?.needsRegistration ||
        response?.needsDeliveryZone ||
        response?.needsDeliveryPricing ||
        response?.delivery?.status !== "ready"
          ? "profile"
          : requestedEntry;
      const isKnownCustomer =
        response?.needsRegistration === false &&
        Boolean(response?.profile?.firstName) &&
        Boolean(response?.address?.street);

      if (isKnownCustomer) {
        setCustomerMatch({
          nextView,
          profile: response.profile,
          address: response.address,
        });
      } else {
        setView(nextView);
      }
    } catch (requestError) {
      setError(requestError?.message || "No pudimos identificarte.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmKnownCustomer = () => {
    const nextView = customerMatch?.nextView || requestedEntry;
    setCustomerMatch(null);
    setView(nextView);
  };

  const changeKnownCustomerAddress = () => {
    setAddressEditOrigin(customerMatch?.address || profileInfo?.address || null);
    setAddressSavePrompt(false);
    setCustomerMatch(null);
    setView("profile");
  };

  const beginProfileEdit = () => {
    setAddressEditOrigin(profileInfo?.address || null);
    setAddressSavePrompt(false);
    setView("profile");
  };

  const rejectKnownCustomer = () => {
    window.sessionStorage.removeItem(getSessionStorageKey(deliveryToken));
    setCustomerMatch(null);
    setAddressEditOrigin(null);
    setAddressSavePrompt(false);
    setSessionToken("");
    setProfile(emptyProfile);
    setProfileInfo(null);
    setDeliveryZoneId("");
    setPhone("");
    setView("identify");
  };

  const saveProfile = async (addressPersistence = "permanent") => {
    if (!sessionToken) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await registerPublicDeliveryCustomer({
        deliveryToken,
        sessionToken,
        profile,
        deliveryZoneId,
        addressPersistence,
      });
      setCoverageCheck(response?.coverage || null);
      applyProfileResponse(response);
      setAddressEditOrigin(null);
      setAddressSavePrompt(false);
      setView(requestedEntry);
    } catch (requestError) {
      setError(requestError?.message || "No pudimos guardar tus datos.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (!sessionToken) return;
    if (!hasDeliveryPricing) {
      setError(
        usesDistanceRates
          ? "Este restaurante todavia no configuro tarifas por distancia. No se puede confirmar un pedido delivery."
          : "Este restaurante todavia no tiene zonas de envio habilitadas. No se puede confirmar un pedido delivery."
      );
      return;
    }
    if (requiresDeliveryZone && !deliveryZoneId) {
      setError("Elegi una zona de envio disponible antes de continuar.");
      return;
    }
    if (
      addressEditOrigin &&
      hasDeliveryAddressChanged(profile, addressEditOrigin)
    ) {
      setAddressSavePrompt(true);
      return;
    }
    await saveProfile("permanent");
  };

  const confirmAddressPersistence = async (addressPersistence) => {
    setAddressSavePrompt(false);
    await saveProfile(addressPersistence);
  };

  const handleValidateAddress = async () => {
    if (!sessionToken) return;
    setValidatingAddress(true);
    setError("");
    try {
      const response = await validatePublicDeliveryAddress({
        deliveryToken,
        sessionToken,
        profile,
      });
      setCoverageCheck(response?.coverage || null);
      if (
        Number.isFinite(Number(response?.coverage?.latitude)) &&
        Number.isFinite(Number(response?.coverage?.longitude))
      ) {
        setProfile((current) => ({
          ...current,
          latitude: Number(response.coverage.latitude),
          longitude: Number(response.coverage.longitude),
        }));
      }
      if (response?.coverage?.covered !== true) {
        setError(
          response?.coverage?.message ||
            "La dirección está fuera del radio de reparto del local."
        );
      }
    } catch (requestError) {
      setCoverageCheck(null);
      setError(requestError?.message || "No pudimos validar la dirección.");
    } finally {
      setValidatingAddress(false);
    }
  };

  const resolveDeliveryLocation = async (location) => {
    if (!sessionToken || !location) return;
    setResolvingLocation(true);
    setError("");
    try {
      const response = await locatePublicDeliveryAddress({
        deliveryToken,
        sessionToken,
        latitude: location.latitude,
        longitude: location.longitude,
      });
      const resolved = response?.location || {};
      setProfile((current) => ({
        ...current,
        street: resolved.street || current.street,
        streetNumber: resolved.streetNumber || current.streetNumber,
        neighborhood: resolved.neighborhood || "",
        city: resolved.city || current.city,
        province: resolved.province || current.province,
        postalCode: resolved.postalCode || current.postalCode,
        latitude: Number(resolved.latitude),
        longitude: Number(resolved.longitude),
      }));
      setCoverageCheck(response?.coverage || null);
      setLocationPickerOpen(false);
      if (response?.coverage?.covered === false) {
        setError(
          response.coverage.message ||
            "La ubicacion elegida esta fuera del radio de reparto."
        );
      }
    } catch (requestError) {
      setError(requestError?.message || "No pudimos guardar esa ubicacion.");
    } finally {
      setResolvingLocation(false);
    }
  };

  const handleUseCurrentLocation = async () => {
    setResolvingLocation(true);
    setError("");
    try {
      const location = await requestCurrentBrowserLocation();
      await resolveDeliveryLocation(location);
    } catch (locationError) {
      setError(locationError?.message || "No pudimos obtener tu ubicacion.");
    } finally {
      setResolvingLocation(false);
    }
  };

  const updateProfileAddress = (field, value) => {
    setCoverageCheck(null);
    setProfile((current) => ({
      ...current,
      [field]: value,
      ...(addressPositionFields.has(field)
        ? { latitude: null, longitude: null }
        : {}),
    }));
  };

  const addSimpleProductToCart = (product) => {
    if (!product?.id) return;
    setCart((current) => {
      const existing = current.find(
        (item) => item.productId === product.id && !item.selectionDetails?.length && !item.notes
      );
      if (!existing) {
        return [
          ...current,
          {
            ...product,
            id: createRequestId(),
            productId: product.id,
            basePrice: toMoney(product.price),
            price: toMoney(product.price),
            quantity: 1,
            selection: createIngredientSelection(product),
            selectionDetails: [],
            notes: "",
          },
        ];
      }
      return current.map((item) =>
        item.id === existing.id ? { ...item, quantity: item.quantity + 1 } : item
      );
    });
  };

  const addPromotionToCart = (promotion) => {
    const promotionItems = Array.isArray(promotion?.items) ? promotion.items : [];
    if (!promotionItems.length) return;

    const missingProduct = promotionItems.find(
      (entry) => !products.some((product) => String(product.id) === String(entry.productId))
    );
    if (missingProduct) {
      setError("Uno de los productos de esta promoción ya no está disponible.");
      return;
    }

    setError("");
    setCart((current) => {
      const nextCart = [...current];
      promotionItems.forEach((entry) => {
        const product = products.find(
          (candidate) => String(candidate.id) === String(entry.productId)
        );
        if (!product) return;
        const quantity = Math.max(1, Number(entry.quantity || 1));
        const existingIndex = nextCart.findIndex(
          (item) =>
            item.productId === product.id &&
            !item.selectionDetails?.length &&
            !item.notes
        );
        if (existingIndex >= 0) {
          nextCart[existingIndex] = {
            ...nextCart[existingIndex],
            quantity: nextCart[existingIndex].quantity + quantity,
          };
          return;
        }
        nextCart.push({
          ...product,
          id: createRequestId(),
          productId: product.id,
          basePrice: toMoney(product.price),
          price: toMoney(product.price),
          quantity,
          selection: createIngredientSelection(product),
          selectionDetails: [],
          notes: "",
        });
      });
      return nextCart;
    });
    setCartOpen(true);
  };

  const requestAddToCart = (product) => {
    if (!product?.id) return;
    if (!isProductCustomizable(product)) {
      addSimpleProductToCart(product);
      return;
    }
    setIngredientSelection(createIngredientSelection(product));
    setCustomizationNotes("");
    setCustomization({ product, cartLineId: "" });
  };

  const editCartItem = (cartItem) => {
    const product = products.find(
      (candidate) => String(candidate.id) === String(cartItem?.productId)
    );
    if (!product || !isProductCustomizable(product)) return;
    setIngredientSelection(createIngredientSelection(product, cartItem.selection));
    setCustomizationNotes(cartItem.notes || "");
    setCustomization({ product, cartLineId: cartItem.id });
  };

  const saveCustomization = () => {
    const product = customization?.product;
    if (!product?.id) return;
    const summary = buildCustomizationSummary(product, ingredientSelection);
    const currentLine = customization.cartLineId
      ? cart.find((item) => item.id === customization.cartLineId)
      : null;
    const cartLine = {
      ...product,
      id: currentLine?.id || createRequestId(),
      productId: product.id,
      basePrice: toMoney(product.price),
      extraPrice: summary.extraPrice,
      price: toMoney(toNumber(product.price) + summary.extraPrice),
      quantity: currentLine?.quantity || Math.max(1, Number(customization.quantity || 1)),
      selection: { ...ingredientSelection },
      selectionDetails: summary.details,
      notes: customizationNotes.trim(),
    };

    setCart((current) =>
      currentLine
        ? current.map((item) => (item.id === currentLine.id ? cartLine : item))
        : [...current, cartLine]
    );
    closeCustomization();
  };

  const updateCartQuantity = (productId, nextQuantity) => {
    setCart((current) =>
      current
        .map((item) =>
          item.id === productId
            ? { ...item, quantity: Math.max(0, Math.min(99, nextQuantity)) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const executeChatOrderDraft = (orderDraft) => {
    if (!orderDraft?.shouldHandle) return null;

    if (orderDraft.status === "not_found") {
      return orderDraft.answer || "No pude identificar con claridad ese producto.";
    }

    const action = String(orderDraft.action || "").trim().toLowerCase();
    if (action === "open_cart") {
      if (cart.length) setCartOpen(true);
      return orderDraft.answer || (cart.length
        ? "Te abro Mi pedido para que lo revises."
        : "Todavía no tenés productos cargados. Te muestro la carta para elegir.");
    }

    if (action === "remove") {
      const requestedId = String(orderDraft.cartItemId || "");
      const requestedName = normalizeMenuSearch(orderDraft.cartProductName || "");
      const target = cart.find(
        (item) =>
          (requestedId && String(item.id) === requestedId) ||
          (requestedName && normalizeMenuSearch(item.name) === requestedName)
      );

      if (!target) {
        if (cart.length) setCartOpen(true);
        return "No encontré ese producto dentro de Mi pedido. Te abro el resumen para revisarlo.";
      }

      setCart((current) => current.filter((item) => item.id !== target.id));
      return `Listo, saqué ${target.name} de Mi pedido.`;
    }

    const draftItems = action === "add_many"
      ? (Array.isArray(orderDraft.items) ? orderDraft.items : [])
      : [orderDraft];

    if (!["add", "add_many"].includes(action) || !draftItems.length) {
      return null;
    }

    const resolvedItems = draftItems
      .map((itemDraft) => {
        const productId = String(itemDraft?.productId || itemDraft?.product?.id || "");
        const product = products.find((candidate) => String(candidate.id) === productId);
        if (!product) return null;
        return {
          itemDraft,
          product,
          quantity: Math.max(
            1,
            Math.min(99, Number(itemDraft.quantity || 1) || 1)
          ),
          ingredientPlan: itemDraft.ingredientSelectionPlan || null,
        };
      })
      .filter(Boolean);

    if (resolvedItems.length !== draftItems.length) {
      return "No encontré uno de esos productos en la carta disponible.";
    }

    const itemNeedingReview = resolvedItems.find(
      ({ product, ingredientPlan }) =>
        ingredientPlan?.needsManualReview ||
        (isProductCustomizable(product) && !ingredientPlan?.hasChanges)
    );

    if (itemNeedingReview) {
      const { product, quantity, ingredientPlan } = itemNeedingReview;
      setIngredientSelection(
        ingredientPlan?.selection || createIngredientSelection(product)
      );
      setCustomizationNotes("");
      setCustomization({ product, cartLineId: "", quantity });
      return ingredientPlan?.hasChanges
        ? `Te abrí ${product.name} con los cambios detectados para que los confirmes antes de agregarlo.`
        : `Te abrí ${product.name} para que revises sus ingredientes antes de agregarlo.`;
    }

    setCart((current) => {
      const nextCart = [...current];

      resolvedItems.forEach(({ product, quantity, ingredientPlan }) => {
        const selection = ingredientPlan?.selection || createIngredientSelection(product);
        const summary = buildCustomizationSummary(product, selection);
        const existingIndex = nextCart.findIndex(
          (item) =>
            item.productId === product.id &&
            JSON.stringify(item.selection || {}) === JSON.stringify(selection) &&
            !item.notes
        );

        if (existingIndex >= 0) {
          nextCart[existingIndex] = {
            ...nextCart[existingIndex],
            quantity: Math.min(99, nextCart[existingIndex].quantity + quantity),
          };
          return;
        }

        nextCart.push({
          ...product,
          id: createRequestId(),
          productId: product.id,
          basePrice: toMoney(product.price),
          extraPrice: summary.extraPrice,
          price: toMoney(toNumber(product.price) + summary.extraPrice),
          quantity,
          selection,
          selectionDetails: summary.details,
          notes: "",
        });
      });

      return nextCart;
    });

    const summary = resolvedItems
      .map(({ product, quantity }) => `${quantity} ${product.name}`)
      .join(", ");
    return `Listo, agregué ${summary} a Mi pedido. Podés abrir el resumen para revisarlo antes de enviarlo.`;
  };

  const handleChat = async (event) => {
    event.preventDefault();
    const question = chatQuestion.trim();
    if (!question || !menuData) return;
    setSubmitting(true);
    setError("");
    setChatQuestion("");
    setChatMessages((current) => [...current, { role: "user", text: question }]);

    try {
      const reply = await createPublicMenuAssistantReply({
        deliveryToken,
        question,
        branch,
        table: { label: "Delivery" },
        products,
        linkedRules: Array.isArray(menuData?.linkedRules) ? menuData.linkedRules : [],
        cartItems: cart,
        customerName: profileInfo?.profile?.firstName || "",
        customerSessionId: sessionToken,
        conversation: chatMessages,
      });
      const related = Array.isArray(reply?.relatedProducts) ? reply.relatedProducts : [];
      const ids = related
        .map((product) => String(product?.id || product?.productId || ""))
        .filter(Boolean);
      const visibleIds = Array.isArray(reply?.visibleProductIds)
        ? reply.visibleProductIds.map(String)
        : [];
      setChatProductIds([...new Set([...ids, ...visibleIds])]);
      const localOrderDraft = buildPublicMenuAssistantOrderDraft({
        question,
        products,
        linkedRules: Array.isArray(menuData?.linkedRules) ? menuData.linkedRules : [],
        cartItems: cart,
      });
      const actionAnswer = executeChatOrderDraft(localOrderDraft);
      setChatMessages((current) => [
        ...current,
        {
          role: "assistant",
          text: actionAnswer || reply?.answer || "No encontré una respuesta útil.",
        },
      ]);
    } catch (requestError) {
      setError(requestError?.message || "El chatbot no pudo responder ahora.");
    } finally {
      setSubmitting(false);
    }
  };

  const getOrderSubmissionError = () => {
    if (!cart.length || !sessionToken) return "No encontramos un pedido para enviar.";
    if (!restaurantOpen) {
      return "El local está cerrado en este momento. Podés volver a intentarlo cuando abra.";
    }
    if (!profileReady) {
      return hasDeliveryPricing
        ? usesDistanceRates
          ? "Valida y guarda tu direccion para calcular el envio antes de confirmar el pedido."
          : "Elegi una zona de envio activa y guarda tus datos antes de confirmar el pedido."
        : usesDistanceRates
          ? "Este restaurante todavia no configuro tarifas por distancia."
          : "Este restaurante todavia no habilito zonas de envio.";
    }
    return "";
  };

  const reviewOrder = (requestedPaymentMethod = "cash_on_delivery") => {
    const validationError = getOrderSubmissionError();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setCartOpen(false);
    setCheckoutReviewPaymentMethod(
      requestedPaymentMethod === "mercado_pago"
        ? "mercado_pago"
        : "cash_on_delivery"
    );
  };

  const sendOrder = async (requestedPaymentMethod = "cash_on_delivery") => {
    if (!cart.length || !sessionToken) return;
    const validationError = getOrderSubmissionError();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const weAuthState = fromWePedidos
        ? await getWeCustomerAuthState().catch(() => null)
        : null;
      const accountKey =
        weAuthState?.profile?.userId || weAuthState?.profile?.email || "";
      const submittedCart = cart.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        notes: item.notes || "",
        selection: item.selection || {},
        selectionDetails: item.selectionDetails || [],
      }));
      const paymentMethod =
        requestedPaymentMethod === "mercado_pago"
          ? "mercado_pago"
          : "cash_on_delivery";
      const response = await createPublicDeliveryOrder({
        deliveryToken,
        sessionToken,
        items: submittedCart,
        paymentMethod,
        expectedTotal: total,
        sourceChannel: fromWePedidos ? "we_pedidos" : view === "chat" ? "chatbot" : "menu_web",
        customerNotes,
        requestId: createRequestId(),
        returnUrl: `${window.location.origin}/we-pedidos/pedidos?payment=delivery`,
        accessToken: weAuthState?.session?.access_token || "",
      });
      if (!response?.ok) {
        throw new Error(response?.message || "No pudimos enviar el pedido.");
      }
      const deeplinkUrl =
        paymentMethod === "mercado_pago"
          ? String(
              response?.payment?.deeplinkUrl ||
                response?.payment?.linkUrl ||
                ""
            ).trim()
          : "";
      if (paymentMethod === "mercado_pago" && !deeplinkUrl) {
        throw new Error(
          "Mercado Pago no devolvio el enlace para pagar el pedido."
        );
      }
      const confirmedOrder = {
        ...(response.order || {}),
        trackingUrl: resolveWePedidosNavigationUrl(
          response.order?.trackingUrl || ""
        ),
      };
      if (fromWePedidos && accountKey && response?.order?.id) {
        rememberWeCustomerOrder({
          accountKey,
          order: {
            id: confirmedOrder.id,
            displayNumber: confirmedOrder.displayNumber,
            restaurantName: branch.name || "Restaurante",
            status: confirmedOrder.status || "received",
            statusLabel:
              paymentMethod === "mercado_pago"
                ? "Esperando pago"
                : "Pedido recibido",
            statusTone: "pending",
            paymentMethod,
            paymentMethodLabel:
              paymentMethod === "mercado_pago"
                ? "Mercado Pago"
                : "Pagar al Rider",
            paymentStatus: confirmedOrder.paymentStatus || "pending",
            paymentStatusLabel:
              paymentMethod === "mercado_pago"
                ? "Completa el pago en Mercado Pago"
                : "Pendiente de pago al Rider",
            subtotal: toMoney(confirmedOrder.subtotal ?? subtotal),
            listSubtotal,
            promotionDiscountTotal: toMoney(
              confirmedOrder.promotionDiscountTotal ?? promotionDiscountTotal
            ),
            deliveryFee: effectiveDeliveryFee,
            total: toMoney(confirmedOrder.total ?? total),
            customerNotes,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            trackingToken: confirmedOrder.trackingToken || "",
            trackingUrl: confirmedOrder.trackingUrl || "",
            items: cart.map((item, index) => ({
              id: `${confirmedOrder.id}:${index}`,
              productName: item.name || "Producto",
              quantity: item.quantity,
              unitPrice: toMoney(item.price),
              lineTotal: toMoney(item.price * item.quantity),
              selectionDetails: item.selectionDetails || [],
              notes: item.notes || "",
            })),
          },
        });
      }
      setConfirmation({ ...confirmedOrder, paymentMethod });
      setCheckoutReviewPaymentMethod("");
      setCartOpen(false);
      setCart([]);
      setCustomerNotes("");
      setView("confirmation");
      if (deeplinkUrl) {
        window.location.assign(deeplinkUrl);
      }
    } catch (requestError) {
      setError(requestError?.message || "No pudimos enviar el pedido.");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setCart([]);
    setChatMessages([]);
    setChatProductIds([]);
    setConfirmation(null);
    setView("choice");
  };

  if (loading) {
    return <main className="delivery-public-shell"><p className="delivery-public-status">Abriendo Delivery...</p></main>;
  }

  if (error && !menuData) {
    return (
      <main className="delivery-public-shell">
        <section className="delivery-public-error-card">
          <p className="delivery-public-eyebrow">Delivery Mozzo</p>
          <h1>No pudimos abrir este Delivery</h1>
          <p>{error}</p>
        </section>
      </main>
    );
  }

  const chatProducts = chatProductIds.length
    ? products.filter((product) => chatProductIds.includes(String(product.id)))
    : [];
  const deliveryPrimaryColor = resolveAccessibleDeliveryBrandColor(
    branch.primaryColor,
    "#b34312"
  );
  const deliverySecondaryColor = resolveAccessibleDeliveryBrandColor(
    branch.secondaryColor,
    "#18243b"
  );

  return (
    <main
      className="delivery-public-shell"
      style={{
        "--delivery-primary": deliveryPrimaryColor,
        "--delivery-secondary": deliverySecondaryColor,
      }}
    >
      <section className="delivery-public-container">
        <header className="delivery-public-header">
          {branch.logoCompactUrl || branch.logoUrl ? (
            <img
              className="delivery-public-logo"
              src={branch.logoCompactUrl || branch.logoUrl}
              alt={branch.name || "Restaurant"}
            />
          ) : null}
          <div>
            <p className="delivery-public-eyebrow">Delivery directo</p>
            <h1>{branch.name || "Tu pedido"}</h1>
            <p>{delivery.welcomeMessage || "Pedi para recibir donde estes."}</p>
          </div>
          <div className="delivery-public-header-actions">
            {fromWePedidos ? (
              <>
                <Link
                  className="delivery-we-brand"
                  to="/we-pedidos"
                  aria-label="Volver a We! pedidos"
                >
                  <img src={wePedidosLogo} alt="" />
                </Link>
                <Link className="delivery-account-orders-link" to="/we-pedidos/pedidos">
                  Mis pedidos
                </Link>
              </>
            ) : null}
            {cart.length ? (
              <button className="delivery-cart-summary" type="button" onClick={() => setCartOpen(true)}>
                {cart.reduce((totalItems, item) => totalItems + item.quantity, 0)} items
                <strong>{moneyFormatter.format(total)}</strong>
              </button>
            ) : null}
          </div>
        </header>

        {error ? <p className="delivery-public-inline-error">{error}</p> : null}
        {menuData && !restaurantOpen ? (
          <div className="delivery-closed-banner" role="status">
            <strong>Local cerrado</strong>
            <span>Podés mirar la carta, pero los pedidos se habilitan cuando el restaurante vuelva a abrir.</span>
          </div>
        ) : null}

        {view === "identify" ? (
          <section className="delivery-public-panel delivery-public-narrow">
            <p className="delivery-public-eyebrow">Primero te identificamos</p>
            <h2>Usa tu numero de WhatsApp</h2>
            <p>Asi recuperamos tu direccion y el costo de envio en tus proximos pedidos.</p>
            <form onSubmit={handleIdentify} className="delivery-public-form">
              <label>
                Numero de WhatsApp
                <input
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  inputMode="tel"
                  placeholder="Ej: 383 4 123456"
                  required
                />
              </label>
              <button className="delivery-primary-button" type="submit" disabled={submitting}>
                {submitting ? "Verificando..." : "Continuar"}
              </button>
            </form>
          </section>
        ) : null}

        {view === "profile" ? (
          <section className="delivery-public-panel">
            <p className="delivery-public-eyebrow">Datos de entrega</p>
            <h2>Contanos donde enviamos tu pedido</h2>
            <p>Los guardamos para que la proxima compra sea mas rapida.</p>
            <form onSubmit={handleSaveProfile} className="delivery-profile-grid">
              <label>
                Nombre
                <input value={profile.firstName} onChange={(event) => setProfile({ ...profile, firstName: event.target.value })} required />
              </label>
                <label>
                  Apellido
                  <input value={profile.lastName} onChange={(event) => setProfile({ ...profile, lastName: event.target.value })} required />
              </label>
              <label>
                Calle
                <input value={profile.street} onChange={(event) => updateProfileAddress("street", event.target.value)} required />
              </label>
                <label>
                  Altura
                  <input value={profile.streetNumber} onChange={(event) => updateProfileAddress("streetNumber", event.target.value)} required />
              </label>
                <label>
                  Barrio
                  <input value={profile.neighborhood} onChange={(event) => updateProfileAddress("neighborhood", event.target.value)} placeholder="Ej.: Centro" required={!profile.city.trim()} />
              </label>
              <label>
                Ciudad
                <input value={profile.city} onChange={(event) => updateProfileAddress("city", event.target.value)} placeholder="Ej.: San Fernando del Valle" required={!profile.neighborhood.trim()} />
              </label>
              <div className="delivery-location-actions">
                <div>
                  <strong>Ubicacion exacta</strong>
                  <span>
                    {profileLocation
                      ? "Punto confirmado para calcular el recorrido."
                      : "Usa tu ubicacion o marca la entrada en el mapa."}
                  </span>
                </div>
                <div>
                  <button
                    type="button"
                    className="delivery-secondary-button"
                    onClick={handleUseCurrentLocation}
                    disabled={resolvingLocation || submitting}
                  >
                    {resolvingLocation ? "Ubicando..." : "Usar ubicacion actual"}
                  </button>
                  <button
                    type="button"
                    className="delivery-secondary-button"
                    onClick={() => setLocationPickerOpen(true)}
                    disabled={resolvingLocation || submitting}
                  >
                    Elegir en el mapa
                  </button>
                </div>
              </div>
              {requiresDeliveryZone ? (
                <label className="delivery-zone-field">
                  Zona de envio
                  <select
                    value={deliveryZoneId}
                    onChange={(event) => setDeliveryZoneId(event.target.value)}
                    required
                    disabled={!hasDeliveryZones}
                  >
                    <option value="">
                      {hasDeliveryZones ? "Elegi la zona donde recibis el pedido" : "Sin zonas de envio habilitadas"}
                    </option>
                    {deliveryZones.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name} · Envio {moneyFormatter.format(toNumber(zone.flatFee))}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {requiresDeliveryZone && selectedDeliveryZone ? (
                <p className="delivery-zone-hint">
                  {selectedDeliveryZone.freeFrom > 0
                    ? `Envio gratis desde ${moneyFormatter.format(toNumber(selectedDeliveryZone.freeFrom))}. `
                    : ""}
                  Tiempo estimado: {selectedDeliveryZone.estimatedMinutes || 35} min.
                </p>
              ) : null}
              {coverageConfig.radiusEnabled === true ? (
                <div className="delivery-map-coverage" role="status">
                  <strong>Radio de reparto: {coverageConfig.maxDistanceKm} km</strong>
                  <span>Verificamos la distancia real por calle antes de confirmar.</span>
                  <button
                    type="button"
                    className="delivery-secondary-button"
                    onClick={handleValidateAddress}
                    disabled={validatingAddress || submitting}
                  >
                    {validatingAddress ? "Validando dirección..." : "Validar dirección"}
                  </button>
                  {coverageCheck ? (
                    <small className={coverageCheck.covered ? "delivery-coverage-ok" : "delivery-coverage-error"}>
                      {coverageCheck.message}
                      {coverageCheck.pricing ? (
                        <>
                          {" "}Envio: {moneyFormatter.format(toNumber(coverageCheck.pricing.flatFee))}
                          {" · "}{coverageCheck.pricing.estimatedMinutes || 35} min.
                        </>
                      ) : null}
                    </small>
                  ) : null}
                </div>
              ) : null}
              {!hasDeliveryPricing ? (
                <p className="delivery-coverage-alert" role="alert">
                  {usesDistanceRates
                    ? "Este restaurante aun no configuro tarifas para las distancias de reparto."
                    : "Este restaurante aun no informo las zonas donde realiza envios. No podes continuar hasta que active su cobertura."}
                </p>
              ) : null}
              <label>
                Piso / departamento
                <input value={`${profile.floor}${profile.unit ? ` ${profile.unit}` : ""}`} onChange={(event) => setProfile({ ...profile, floor: event.target.value, unit: "" })} />
              </label>
              <label>
                Referencia para el repartidor
                <input value={profile.reference} onChange={(event) => setProfile({ ...profile, reference: event.target.value })} />
              </label>
              <button className="delivery-primary-button" type="submit" disabled={submitting}>
                {submitting ? "Guardando..." : "Guardar y continuar"}
              </button>
            </form>
          </section>
        ) : null}

        {view === "choice" ? (
          <section className="delivery-public-choice">
            <div className="delivery-profile-summary">
              <div>
                <p className="delivery-public-eyebrow">Hola {profileInfo?.profile?.firstName || ""}</p>
                <h2>{profileInfo?.address?.street || "Direccion pendiente"} {profileInfo?.address?.streetNumber || ""}</h2>
                {profileReady ? (
                  <p>
                    Envio: {effectiveDeliveryFee ? moneyFormatter.format(effectiveDeliveryFee) : "sin cargo"}
                    {profileInfo?.delivery?.estimatedMinutes ? ` · ${profileInfo.delivery.estimatedMinutes} min aprox.` : ""}
                  </p>
                ) : (
                  <p className="delivery-zone-pending">{profileInfo?.delivery?.message}</p>
                )}
              </div>
              <button type="button" className="delivery-secondary-button" onClick={beginProfileEdit}>Editar datos</button>
            </div>
            <div className="delivery-choice-grid">
              <button type="button" className="delivery-choice-card" onClick={() => setView("menu")}>
                <span>01</span>
                <h2>Ver menu Delivery</h2>
                <p>Explora la carta, agrega productos y arma tu pedido.</p>
              </button>
              {delivery.chatbotEnabled !== false ? (
                <button type="button" className="delivery-choice-card delivery-choice-card-dark" onClick={() => setView("chat")}>
                  <span>02</span>
                  <h2>Hablar con el chatbot</h2>
                  <p>Decile lo que queres y te ayuda a encontrar opciones del menu.</p>
                </button>
              ) : null}
            </div>
          </section>
        ) : null}

        {view === "menu" ? (
          <section className="delivery-menu-layout">
            <aside className="delivery-menu-sidebar">
              <button type="button" className="delivery-back-button" onClick={() => setView("choice")}>Volver</button>
              <h2>Menu</h2>
              <button type="button" className={!selectedCategoryId ? "delivery-category active" : "delivery-category"} onClick={() => setSelectedCategoryId("")}>Todo</button>
              {categories.map((category) => (
                <button key={category.id} type="button" className={selectedCategoryId === category.id ? "delivery-category active" : "delivery-category"} onClick={() => setSelectedCategoryId(category.id)}>
                  {category.name}
                </button>
              ))}
              {delivery.chatbotEnabled !== false ? <button type="button" className="delivery-secondary-button" onClick={() => setView("chat")}>Usar chatbot</button> : null}
            </aside>
            <div className="delivery-products-area">
              <div className="delivery-products-heading">
                <div>
                  <p className="delivery-public-eyebrow">Carta de Delivery</p>
                  <h2>{selectedCategoryId ? categories.find((category) => category.id === selectedCategoryId)?.name : "Todo el menu"}</h2>
                </div>
                <span>{visibleProducts.length + visiblePromotions.length} opciones</span>
              </div>
              <label className="delivery-menu-search">
                <span aria-hidden="true">⌕</span>
                <input
                  value={menuSearch}
                  onChange={(event) => setMenuSearch(event.target.value)}
                  placeholder="Buscar un plato"
                />
                {menuSearch ? (
                  <button type="button" onClick={() => setMenuSearch("")} aria-label="Limpiar búsqueda">×</button>
                ) : null}
              </label>
              {visiblePromotions.length ? (
                <section className="delivery-promotions" aria-labelledby="delivery-promotions-title">
                  <div className="delivery-promotions-heading">
                    <div>
                      <p className="delivery-public-eyebrow">Beneficios vigentes</p>
                      <h3 id="delivery-promotions-title">Promos, combos y menú del día</h3>
                    </div>
                    <span>El ahorro se calcula automáticamente</span>
                  </div>
                  <div className="delivery-promotion-grid">
                    {visiblePromotions.map((promotion) => {
                      const selectable =
                        promotion.type === "combo" &&
                        Array.isArray(promotion.items) &&
                        promotion.items.length > 0;
                      const promotionTypeLabel = isDailyMenuPromotion(promotion)
                        ? "Menú del día"
                        : selectable
                          ? "Combo"
                          : "Promoción";
                      return (
                        <article className="delivery-promotion-card" key={promotion.id}>
                          <div className="delivery-promotion-media">
                            {promotion.imageUrl ? (
                              <img src={promotion.imageUrl} alt="" />
                            ) : (
                              <span aria-hidden="true">
                                {String(promotion.title || "P").slice(0, 1)}
                              </span>
                            )}
                            <b>{promotionTypeLabel}</b>
                          </div>
                          <div className="delivery-promotion-body">
                            <p>{promotion.hook || promotion.pricingSummary || promotionTypeLabel}</p>
                            <h3>{promotion.title}</h3>
                            <small>
                              {promotion.detail ||
                                (Array.isArray(promotion.items)
                                  ? promotion.items
                                      .map((item) => `${item.quantity || 1} ${item.name || "producto"}`)
                                      .join(" + ")
                                  : "Se aplica a los productos alcanzados.")}
                            </small>
                          </div>
                          <div className="delivery-promotion-footer">
                            <strong>
                              {selectable && toNumber(promotion.comboPrice) > 0
                                ? moneyFormatter.format(toNumber(promotion.comboPrice))
                                : promotion.pricingSummary || "Descuento automático"}
                              {promotion.paymentCondition === "cash_only" ? " · Solo efectivo" : ""}
                            </strong>
                            {selectable ? (
                              <button type="button" onClick={() => addPromotionToCart(promotion)}>
                                Agregar
                              </button>
                            ) : (
                              <span>Se aplica solo</span>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              <div className="delivery-product-grid">
                {visibleProducts.map((product) => (
                  <article key={product.id} className="delivery-product-card">
                    <button
                      type="button"
                      className="delivery-product-media"
                      onClick={() => requestAddToCart(product)}
                      aria-label={`${isProductCustomizable(product) ? "Elegir" : "Agregar"} ${product.name}`}
                    >
                      <CatalogImage src={product.imageUrl} alt="" />
                    </button>
                    <div>
                      <p>{product.categoryName || product.subcategoryName}</p>
                      <h3>{product.name}</h3>
                      <small>{product.description || "Preparado en el momento."}</small>
                    </div>
                    <div className="delivery-product-footer">
                      <strong>{moneyFormatter.format(toNumber(product.price))}</strong>
                      <button type="button" onClick={() => requestAddToCart(product)}>
                        {isProductCustomizable(product) ? "Elegir" : "Agregar"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
              {!visibleProducts.length && !visiblePromotions.length ? (
                <div className="delivery-products-empty">
                  <strong>No encontramos ese plato</strong>
                  <span>Probá con otro nombre o elegí otra categoría.</span>
                </div>
              ) : null}
              <DeliveryCart
                cart={cart}
                listSubtotal={listSubtotal}
                promotionDiscountTotal={promotionDiscountTotal}
                subtotal={subtotal}
                deliveryFee={effectiveDeliveryFee}
                total={total}
                profileReady={profileReady}
                hasDeliveryZones={hasDeliveryPricing}
                usesDistanceRates={usesDistanceRates}
                customerNotes={customerNotes}
                onNotesChange={setCustomerNotes}
                onQuantityChange={updateCartQuantity}
                onEditItem={editCartItem}
                onSendOrder={reviewOrder}
                mercadoPagoAvailable={mercadoPagoAvailable}
                mercadoPagoMessage={paymentAvailability.mercadoPagoMessage}
                sending={submitting}
                restaurantOpen={restaurantOpen}
              />
            </div>
          </section>
        ) : null}

        {view === "chat" ? (
          <section className="delivery-chat-layout">
            <div className="delivery-chat-panel">
              <div className="delivery-chat-head">
                <div>
                  <p className="delivery-public-eyebrow">Chatbot de Delivery</p>
                  <h2>Te ayudo a armar el pedido</h2>
                </div>
                <button type="button" className="delivery-back-button" onClick={() => setView("choice")}>Volver</button>
              </div>
              <div className="delivery-chat-history">
                {!chatMessages.length ? <p>Ejemplo: "Quiero una milanesa con papas y una gaseosa".</p> : null}
                {chatMessages.map((message, index) => (
                  <p key={`${message.role}-${index}`} className={`delivery-chat-message ${message.role}`}>{message.text}</p>
                ))}
              </div>
              {chatProducts.length ? (
                <div className="delivery-chat-products">
                  {chatProducts.map((product) => (
                    <button key={product.id} type="button" onClick={() => requestAddToCart(product)}>
                      <CatalogImage src={product.imageUrl} alt="" />
                      <span><strong>{product.name}</strong><small>{moneyFormatter.format(toNumber(product.price))}</small></span>
                      <b>{isProductCustomizable(product) ? "Elegir" : "Agregar"}</b>
                    </button>
                  ))}
                </div>
              ) : null}
              <form className="delivery-chat-form" onSubmit={handleChat}>
                <input value={chatQuestion} onChange={(event) => setChatQuestion(event.target.value)} placeholder="Escribi tu pedido o consulta" disabled={submitting} />
                <button className="delivery-primary-button" type="submit" disabled={submitting}>{submitting ? "Pensando..." : "Enviar"}</button>
              </form>
            </div>
            <DeliveryCart
              cart={cart}
              listSubtotal={listSubtotal}
              promotionDiscountTotal={promotionDiscountTotal}
              subtotal={subtotal}
              deliveryFee={effectiveDeliveryFee}
              total={total}
              profileReady={profileReady}
              hasDeliveryZones={hasDeliveryPricing}
              usesDistanceRates={usesDistanceRates}
              customerNotes={customerNotes}
              onNotesChange={setCustomerNotes}
              onQuantityChange={updateCartQuantity}
              onEditItem={editCartItem}
              onSendOrder={reviewOrder}
              mercadoPagoAvailable={mercadoPagoAvailable}
              mercadoPagoMessage={paymentAvailability.mercadoPagoMessage}
              sending={submitting}
              compact
              restaurantOpen={restaurantOpen}
            />
          </section>
        ) : null}

        {view === "confirmation" ? (
          <section className="delivery-public-panel delivery-public-confirmation">
            <p className="delivery-public-eyebrow">Pedido enviado</p>
            <h2>{confirmation?.displayNumber || "Pedido confirmado"}</h2>
            <p>
              {confirmation?.paymentMethod === "mercado_pago"
                ? "Abrimos Mercado Pago para completar el pago del pedido."
                : "Vas a pagarle al Rider al recibir: podras elegir efectivo o QR."}
            </p>
            <strong>{moneyFormatter.format(toNumber(confirmation?.total))}</strong>
            <div className="delivery-confirmation-actions">
              {confirmation?.trackingUrl ? (
                <Link className="delivery-primary-button" to={confirmation.trackingUrl}>
                  Seguir mi entrega
                </Link>
              ) : null}
              <button type="button" className="delivery-secondary-button" onClick={restart}>Hacer otro pedido</button>
              {fromWePedidos ? (
                <Link className="delivery-link-button" to="/we-pedidos/pedidos">
                  Ver mis pedidos
                </Link>
              ) : null}
            </div>
          </section>
        ) : null}

        {customization?.product ? (
          <ProductCustomization
            product={customization.product}
            selection={ingredientSelection}
            notes={customizationNotes}
            editing={Boolean(customization.cartLineId)}
            onSelectionChange={setIngredientSelection}
            onNotesChange={setCustomizationNotes}
            onClose={closeCustomization}
            onSave={saveCustomization}
          />
        ) : null}

        {checkoutReviewPaymentMethod ? (
          <DeliveryOrderReview
            branchName={branch.name || "Restaurante"}
            profile={profile}
            phone={phone}
            cart={cart}
            customerNotes={customerNotes}
            listSubtotal={listSubtotal}
            promotionDiscountTotal={promotionDiscountTotal}
            subtotal={subtotal}
            deliveryFee={effectiveDeliveryFee}
            total={total}
            paymentMethod={checkoutReviewPaymentMethod}
            promotionNotice={promotionPricing.paymentPrices
              ? `Productos: ${moneyFormatter.format(promotionPricing.paymentPrices.cash.finalTotal)} si el restaurante cobra en efectivo; ${moneyFormatter.format(promotionPricing.paymentPrices.other.finalTotal)} si cobra por otro medio.`
              : ""}
            sending={submitting}
            onEdit={() => {
              closeCheckoutReview();
              setCartOpen(true);
            }}
            onClose={closeCheckoutReview}
            onConfirm={() => void sendOrder(checkoutReviewPaymentMethod)}
          />
        ) : null}

        {["menu", "chat"].includes(view) && cart.length && !cartOpen ? (
          <button
            type="button"
            className="delivery-floating-cart"
            onClick={() => setCartOpen(true)}
            aria-label="Abrir y confirmar mi pedido"
          >
            <span>{cart.reduce((totalItems, item) => totalItems + item.quantity, 0)}</span>
            <b>Ver mi pedido</b>
            <strong>{moneyFormatter.format(total)}</strong>
          </button>
        ) : null}

        {cartOpen && cart.length ? (
          <div
            className="delivery-cart-dialog-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setCartOpen(false);
            }}
          >
            <section className="delivery-cart-dialog" role="dialog" aria-modal="true" aria-labelledby="delivery-cart-dialog-title">
              <header>
                <div>
                  <p className="delivery-public-eyebrow">Resumen</p>
                  <h2 id="delivery-cart-dialog-title">Confirmá tu pedido</h2>
                </div>
                <button type="button" onClick={() => setCartOpen(false)} aria-label="Cerrar pedido">×</button>
              </header>
              <DeliveryCart
                cart={cart}
                listSubtotal={listSubtotal}
                promotionDiscountTotal={promotionDiscountTotal}
                subtotal={subtotal}
                deliveryFee={effectiveDeliveryFee}
                total={total}
                profileReady={profileReady}
                hasDeliveryZones={hasDeliveryPricing}
                usesDistanceRates={usesDistanceRates}
                customerNotes={customerNotes}
                onNotesChange={setCustomerNotes}
                onQuantityChange={updateCartQuantity}
                onEditItem={(item) => {
                  setCartOpen(false);
                  editCartItem(item);
                }}
                onSendOrder={reviewOrder}
                mercadoPagoAvailable={mercadoPagoAvailable}
                mercadoPagoMessage={paymentAvailability.mercadoPagoMessage}
                sending={submitting}
                restaurantOpen={restaurantOpen}
                compact
              />
            </section>
          </div>
        ) : null}

        <DeliveryLocationPicker
          open={locationPickerOpen}
          initialLocation={profileLocation}
          restaurantLocation={restaurantLocation}
          onClose={() => setLocationPickerOpen(false)}
          onConfirm={resolveDeliveryLocation}
          onLocateCurrent={requestCurrentBrowserLocation}
          resolving={resolvingLocation}
        />

        {customerMatch ? (
          <div className="delivery-customer-match-backdrop" role="presentation">
            <section
              className="delivery-customer-match-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delivery-customer-match-title"
            >
              <p className="delivery-public-eyebrow">Encontramos tus datos</p>
              <h2 id="delivery-customer-match-title">
                ¿Este numero corresponde a {customerMatch.profile?.firstName} {customerMatch.profile?.lastName}?
              </h2>
              <div className="delivery-customer-match-address">
                <span>Direccion guardada</span>
                <strong>
                  {customerMatch.address?.street} {customerMatch.address?.streetNumber || ""}
                </strong>
                {[customerMatch.address?.floor, customerMatch.address?.unit, customerMatch.address?.neighborhood, customerMatch.address?.city]
                  .filter(Boolean)
                  .length ? (
                    <small>
                      {[customerMatch.address?.floor, customerMatch.address?.unit, customerMatch.address?.neighborhood, customerMatch.address?.city]
                        .filter(Boolean)
                        .join(" | ")}
                    </small>
                  ) : null}
              </div>
              <div className="delivery-customer-match-actions">
                <button type="button" className="delivery-primary-button" onClick={confirmKnownCustomer}>
                  Si, usar esta direccion
                </button>
                <button type="button" className="delivery-secondary-button" onClick={changeKnownCustomerAddress}>
                  Cambiar direccion
                </button>
                <button type="button" className="delivery-customer-match-reject" onClick={rejectKnownCustomer}>
                  No soy esta persona
                </button>
              </div>
            </section>
          </div>
        ) : null}

        {addressSavePrompt ? (
          <div className="delivery-customer-match-backdrop" role="presentation">
            <section
              className="delivery-customer-match-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delivery-address-persistence-title"
            >
              <p className="delivery-public-eyebrow">Nueva direccion</p>
              <h2 id="delivery-address-persistence-title">
                ¿Queres guardar este cambio para siempre o usarlo solo en este pedido?
              </h2>
              <div className="delivery-customer-match-address">
                <span>Direccion nueva</span>
                <strong>{profile.street} {profile.streetNumber}</strong>
                {[profile.floor, profile.unit, profile.neighborhood, profile.city]
                  .filter(Boolean)
                  .length ? (
                    <small>
                      {[profile.floor, profile.unit, profile.neighborhood, profile.city]
                        .filter(Boolean)
                        .join(" | ")}
                    </small>
                  ) : null}
              </div>
              <p className="delivery-address-persistence-help">
                Si elegis solo este pedido, tu direccion habitual no se modifica.
              </p>
              <div className="delivery-customer-match-actions">
                <button
                  type="button"
                  className="delivery-primary-button"
                  onClick={() => confirmAddressPersistence("temporary")}
                  disabled={submitting}
                >
                  Solo para este pedido
                </button>
                <button
                  type="button"
                  className="delivery-secondary-button"
                  onClick={() => confirmAddressPersistence("permanent")}
                  disabled={submitting}
                >
                  Guardar como habitual
                </button>
                <button
                  type="button"
                  className="delivery-customer-match-reject"
                  onClick={() => setAddressSavePrompt(false)}
                  disabled={submitting}
                >
                  Volver a editar
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </section>
    </main>
  );
}

function DeliveryCart({
  cart,
  listSubtotal,
  promotionDiscountTotal,
  subtotal,
  deliveryFee,
  total,
  profileReady,
  hasDeliveryZones,
  usesDistanceRates,
  customerNotes,
  onNotesChange,
  onQuantityChange,
  onEditItem,
  onSendOrder,
  mercadoPagoAvailable = false,
  mercadoPagoMessage = "",
  sending,
  restaurantOpen = true,
  compact = false,
}) {
  return (
    <aside className={`delivery-cart ${compact ? "compact" : ""}`}>
      <p className="delivery-public-eyebrow">Tu pedido</p>
      {!cart.length ? <p className="delivery-cart-empty">Todavia no agregaste productos.</p> : null}
      {cart.map((item) => (
        <div className="delivery-cart-line" key={item.id}>
          <div className="delivery-cart-product">
            <strong>{item.name}</strong>
            {item.selectionDetails?.length ? (
              <small className="delivery-cart-customization">
                {item.selectionDetails.join(" | ")}
              </small>
            ) : null}
            {item.notes ? <small>Nota: {item.notes}</small> : null}
            <small>{moneyFormatter.format(toNumber(item.price))} c/u</small>
            {isProductCustomizable(item) ? (
              <button
                type="button"
                className="delivery-cart-edit"
                onClick={() => onEditItem(item)}
              >
                Modificar ingredientes
              </button>
            ) : null}
          </div>
          <div className="delivery-cart-quantity">
            <button type="button" onClick={() => onQuantityChange(item.id, item.quantity - 1)}>-</button>
            <span>{item.quantity}</span>
            <button type="button" onClick={() => onQuantityChange(item.id, item.quantity + 1)}>+</button>
          </div>
        </div>
      ))}
      {cart.length ? (
        <>
          <label className="delivery-notes-field">Indicaciones del pedido
            <textarea value={customerNotes} onChange={(event) => onNotesChange(event.target.value)} placeholder="Ej: timbre roto, llamar al llegar" rows="2" />
          </label>
          <div className="delivery-total-line"><span>Productos</span><strong>{moneyFormatter.format(listSubtotal)}</strong></div>
          {promotionDiscountTotal > 0 ? (
            <>
              <div className="delivery-total-line promotion">
                <span>Promociones</span>
                <strong>-{moneyFormatter.format(promotionDiscountTotal)}</strong>
              </div>
              <div className="delivery-total-line">
                <span>Subtotal con descuento</span>
                <strong>{moneyFormatter.format(subtotal)}</strong>
              </div>
            </>
          ) : null}
          <div className="delivery-total-line"><span>Envio</span><strong>{profileReady ? moneyFormatter.format(deliveryFee) : "Pendiente"}</strong></div>
          <div className="delivery-total-line total"><span>Total</span><strong>{profileReady ? moneyFormatter.format(total) : "A confirmar"}</strong></div>
          {!profileReady ? (
            <p className="delivery-zone-pending">
              {hasDeliveryZones
                ? usesDistanceRates
                  ? "Valida y guarda la direccion para calcular el costo por distancia."
                  : "Elegi una zona de envio activa y guarda la direccion para calcular el total."
                : usesDistanceRates
                  ? "El restaurante todavia no configuro tarifas por distancia."
                  : "El restaurante todavia no habilito zonas de envio para Delivery."}
            </p>
          ) : null}
          <div className="delivery-payment-choice" aria-label="Forma de pago">
            <button
              type="button"
              className="delivery-primary-button"
              onClick={() => onSendOrder("mercado_pago")}
              disabled={!profileReady || sending || !restaurantOpen || !mercadoPagoAvailable}
              title={!mercadoPagoAvailable ? mercadoPagoMessage : undefined}
            >
              {sending
                ? "Preparando revision..."
                : restaurantOpen
                  ? "Revisar y pagar con Mercado Pago"
                  : "Local cerrado"}
            </button>
            <button
              type="button"
              className="delivery-secondary-button"
              onClick={() => onSendOrder("cash_on_delivery")}
              disabled={!profileReady || sending || !restaurantOpen}
            >
              Revisar pedido · Pagar al Rider
            </button>
          </div>
          {!mercadoPagoAvailable && mercadoPagoMessage ? (
            <p className="delivery-zone-pending">{mercadoPagoMessage}</p>
          ) : null}
        </>
      ) : null}
    </aside>
  );
}

export function DeliveryOrderReview({
  branchName,
  profile,
  phone,
  cart,
  customerNotes,
  listSubtotal,
  promotionDiscountTotal,
  subtotal,
  deliveryFee,
  total,
  paymentMethod,
  promotionNotice = "",
  sending,
  onEdit,
  onClose,
  onConfirm,
}) {
  const [reviewed, setReviewed] = useState(false);
  useEffect(() => setReviewed(false), [total, paymentMethod, promotionNotice, cart, customerNotes]);
  const address = formatDeliveryCheckoutAddress(profile);
  const customerName = [profile.firstName, profile.lastName]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
  const paymentLabel =
    paymentMethod === "mercado_pago"
      ? "Mercado Pago ahora"
      : "Al Rider al recibir, en efectivo o QR";

  return (
    <div className="delivery-order-review-backdrop" role="presentation">
      <section
        className="delivery-order-review"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-order-review-title"
      >
        <header>
          <div>
            <p className="delivery-public-eyebrow">Último paso</p>
            <h2 id="delivery-order-review-title">Revisá bien tu pedido</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar revisión">×</button>
        </header>

        <div className="delivery-order-review-content">
          <section className="delivery-review-block">
            <h3>Datos de entrega</h3>
            <dl>
              <div><dt>Restaurante</dt><dd>{branchName}</dd></div>
              <div><dt>Recibe</dt><dd>{customerName || "Cliente"}</dd></div>
              <div><dt>Teléfono</dt><dd>{phone || "No informado"}</dd></div>
              <div><dt>Dirección</dt><dd>{address || "No informada"}</dd></div>
              {profile.reference ? <div><dt>Referencia</dt><dd>{profile.reference}</dd></div> : null}
            </dl>
          </section>

          <section className="delivery-review-block">
            <h3>Productos</h3>
            <ul className="delivery-review-items">
              {cart.map((item) => (
                <li key={item.id}>
                  <div>
                    <strong>{item.quantity}× {item.name}</strong>
                    {item.selectionDetails?.length ? (
                      <small>{item.selectionDetails.join(" | ")}</small>
                    ) : null}
                    {item.notes ? <small>Nota: {item.notes}</small> : null}
                  </div>
                  <b>{moneyFormatter.format(toNumber(item.price) * Number(item.quantity || 0))}</b>
                </li>
              ))}
            </ul>
            {customerNotes ? (
              <p className="delivery-review-notes"><strong>Indicaciones:</strong> {customerNotes}</p>
            ) : null}
          </section>

          <section className="delivery-review-block delivery-review-summary">
            <h3>Pago</h3>
            {promotionNotice ? <p role="status">{promotionNotice} La condicion depende de como cobra el restaurante. Si el rider cambia ese medio, debe acordar el nuevo total contigo antes de pagar.</p> : null}
            <div><span>Productos</span><strong>{moneyFormatter.format(listSubtotal)}</strong></div>
            {promotionDiscountTotal > 0 ? (
              <>
                <div className="is-discount"><span>Promociones</span><strong>-{moneyFormatter.format(promotionDiscountTotal)}</strong></div>
                <div><span>Subtotal</span><strong>{moneyFormatter.format(subtotal)}</strong></div>
              </>
            ) : null}
            <div><span>Envío</span><strong>{moneyFormatter.format(deliveryFee)}</strong></div>
            <div><span>Forma de pago</span><strong>{paymentLabel}</strong></div>
            <div className="delivery-review-total"><span>Total</span><strong>{moneyFormatter.format(total)}</strong></div>
          </section>

          <label className="delivery-review-check">
            <input
              type="checkbox"
              checked={reviewed}
              onChange={(event) => setReviewed(event.target.checked)}
              disabled={sending}
            />
            <span>Revisé los productos, la dirección y la forma de pago.</span>
          </label>
        </div>

        <footer>
          <button type="button" className="delivery-secondary-button" onClick={onEdit} disabled={sending}>
            Corregir pedido
          </button>
          <button
            type="button"
            className="delivery-primary-button"
            onClick={onConfirm}
            disabled={!reviewed || sending}
          >
            {sending
              ? "Enviando..."
              : paymentMethod === "mercado_pago"
                ? "Confirmar e ir a Mercado Pago"
                : "Confirmar y enviar pedido"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function ProductCustomization({
  product,
  selection,
  notes,
  editing,
  onSelectionChange,
  onNotesChange,
  onClose,
  onSave,
}) {
  const ingredients = getProductIngredients(product);
  const summary = buildCustomizationSummary(product, selection);
  const totalPrice = toMoney(toNumber(product.price) + summary.extraPrice);

  const changeIngredientQuantity = (ingredient, delta) => {
    const ingredientId = String(ingredient.id);
    const baseQuantity = getIngredientBaseQuantity(ingredient);
    const minimumQuantity = ingredient.canRemove ? 0 : baseQuantity;
    const maximumQuantity = getIngredientMaximumQuantity(ingredient);
    const currentQuantity = Number.isFinite(Number(selection[ingredientId]))
      ? Number(selection[ingredientId])
      : baseQuantity;
    const nextQuantity = Math.max(
      minimumQuantity,
      Math.min(maximumQuantity, currentQuantity + delta)
    );
    onSelectionChange((current) => ({ ...current, [ingredientId]: nextQuantity }));
  };

  return (
    <div className="delivery-customization-backdrop" role="presentation">
      <section
        className="delivery-customization-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-customization-title"
      >
        <header className="delivery-customization-header">
          <div>
            <p className="delivery-public-eyebrow">Personaliza tu plato</p>
            <h2 id="delivery-customization-title">{product.name}</h2>
            <span>Quita o agrega ingredientes antes de sumarlo al pedido.</span>
          </div>
          <button type="button" className="delivery-customization-close" onClick={onClose} aria-label="Cerrar">
            X
          </button>
        </header>

        <div className="delivery-customization-list">
          {ingredients.map((ingredient) => {
            const ingredientId = String(ingredient.id);
            const baseQuantity = getIngredientBaseQuantity(ingredient);
            const selectedQuantity = Number.isFinite(Number(selection[ingredientId]))
              ? Number(selection[ingredientId])
              : baseQuantity;
            const minimumQuantity = ingredient.canRemove ? 0 : baseQuantity;
            const maximumQuantity = getIngredientMaximumQuantity(ingredient);
            const isFixed = minimumQuantity === maximumQuantity;
            const extraUnitPrice = toNumber(ingredient.price);

            return (
              <article className="delivery-customization-ingredient" key={ingredientId}>
                <CatalogImage src={ingredient.imageUrl} alt="" />
                <div className="delivery-customization-ingredient-info">
                  <strong>{ingredient.name}</strong>
                  <small>
                    Base: {baseQuantity}
                    {ingredient.canRemove ? " | Se puede quitar" : " | Incluido fijo"}
                  </small>
                  {ingredient.canAddMore ? (
                    <small>
                      Extra: {moneyFormatter.format(extraUnitPrice)} | Maximo {maximumQuantity}
                    </small>
                  ) : null}
                </div>
                {isFixed ? (
                  <span className="delivery-customization-fixed">Fijo</span>
                ) : (
                  <div className="delivery-customization-controls">
                    <button
                      type="button"
                      onClick={() => changeIngredientQuantity(ingredient, -1)}
                      disabled={selectedQuantity <= minimumQuantity}
                      aria-label={`Quitar ${ingredient.name}`}
                    >
                      -
                    </button>
                    <span>{selectedQuantity}</span>
                    <button
                      type="button"
                      onClick={() => changeIngredientQuantity(ingredient, 1)}
                      disabled={selectedQuantity >= maximumQuantity}
                      aria-label={`Agregar ${ingredient.name}`}
                    >
                      +
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <label className="delivery-customization-notes">
          Aclaracion para este plato
          <textarea
            value={notes}
            onChange={(event) => onNotesChange(event.target.value)}
            placeholder="Ej: bien cocido, salsa aparte"
            rows="2"
            maxLength="500"
          />
        </label>

        <footer className="delivery-customization-footer">
          <div>
            {summary.details.length ? <small>{summary.details.join(" | ")}</small> : <small>Preparacion normal</small>}
            <strong>{moneyFormatter.format(totalPrice)}</strong>
          </div>
          <button type="button" className="delivery-secondary-button" onClick={onClose}>Cancelar</button>
          <button type="button" className="delivery-primary-button" onClick={onSave}>
            {editing ? "Guardar cambios" : "Agregar al pedido"}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default DeliveryPublic;
